import { Field, NumberInput, SingleSelectDropdown, TextInput } from "@carbon6/c6-frontend-design";
import { appTextModes } from "@/shared/ui/text";
import { textOrNull } from "@/shared/utils/text";
import type { HandRole } from "../../../domain/mapping";
import { EvidenceDetails } from "./EvidenceDetails";
import styles from "./MappingForm.module.css";

type Props = { role: HandRole; onChange: (update: (role: HandRole) => HandRole) => void };

function Choice<T extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: T | null;
  options: readonly (readonly [T, string])[];
  onChange: (value: T | null) => void;
}) {
  return (
    <Field textVariant={appTextModes["body-normal"]} label={label} layout="stack">
      <SingleSelectDropdown
        fullWidth
        label={label}
        size="md"
        value={value ?? ""}
        options={[
          { value: "", label: "미확인" },
          ...options.map(([value, label]) => ({ value, label })),
        ]}
        onValueChange={(next) => onChange(next === "" ? null : (next as T))}
      />
    </Field>
  );
}

export function TcpForm({ role, onChange }: Props) {
  const tcp = role.tcp;
  return (
    <fieldset className={styles.group} data-mapping-field="tcp pose_kind">
      <legend>TCP · 원본 S에서 닫힘 C로</legend>
      <p className={styles.caption}>
        이동량은 원본 도구 축 기준 mm입니다. 손 축 보정과 별도로 적용합니다. 미확인 값은 비워
        둡니다.
      </p>
      <Choice
        label="포즈 종류"
        value={role.pose_kind}
        options={[
          ["absolute", "절대 포즈"],
          ["relative", "상대 포즈 · 변환 불가"],
        ]}
        onChange={(pose_kind) => onChange((current) => ({ ...current, pose_kind }))}
      />
      <Choice
        label="원본 기준점 S"
        value={tcp.origin.point}
        options={[
          ["flange", "플랜지"],
          ["sigmakit_closed_tcp", "이미 닫힘 TCP"],
          ["other_fixed", "다른 고정 기준점"],
          ["grip_center", "개폐에 따라 변하는 파지 중심 · 변환 불가"],
        ]}
        onChange={(point) =>
          onChange((current) => ({
            ...current,
            tcp: { ...current.tcp, origin: { ...current.tcp.origin, point } },
          }))
        }
      />
      <TextInput
        fullWidth
        aria-label="원본 기준점 정의"
        placeholder="다른 고정 기준점의 정의"
        value={tcp.origin.definition ?? ""}
        onChange={(event) =>
          onChange((current) => ({
            ...current,
            tcp: {
              ...current.tcp,
              origin: { ...current.tcp.origin, definition: textOrNull(event.target.value) },
            },
          }))
        }
      />
      <Choice
        label="닫힘 TCP로의 변환"
        value={tcp.transform.kind}
        options={[
          ["identity", "이미 닫힘 TCP · 이동 없음"],
          ["fixed", "고정 이동 · 원본 도구 축"],
        ]}
        onChange={(kind) =>
          onChange((current) => ({
            ...current,
            tcp: {
              ...current.tcp,
              transform: {
                ...current.tcp.transform,
                kind,
                axes: kind === "fixed" ? "source_tool" : null,
                x_mm: null,
                y_mm: null,
                z_mm: null,
              },
            },
          }))
        }
      />
      {tcp.transform.kind !== "fixed" ? null : (
        <div className={styles.form}>
          {(["x_mm", "y_mm", "z_mm"] as const).map((key) => (
            <Field
              key={key}
              textVariant={appTextModes["body-normal"]}
              label={`${key[0].toUpperCase()} 이동(mm)`}
              layout="stack"
            >
              <NumberInput
                fullWidth
                mode="decimal"
                value={tcp.transform[key]}
                aria-label={`${key[0].toUpperCase()} TCP 이동(mm)`}
                onChange={(value) =>
                  onChange((current) => ({
                    ...current,
                    tcp: { ...current.tcp, transform: { ...current.tcp.transform, [key]: value } },
                  }))
                }
              />
            </Field>
          ))}
        </div>
      )}
      <Choice
        label="원본에 이미 적용한 보정"
        value={tcp.applied_correction.state}
        options={[
          ["native_target", "원래 닫힘 TCP"],
          ["applied_to_target", "닫힘 TCP로 이미 보정함"],
          ["not_applied", "아직 보정하지 않음"],
          ["other_applied", "다른 보정을 적용함"],
        ]}
        onChange={(state) =>
          onChange((current) => ({
            ...current,
            tcp: {
              ...current.tcp,
              applied_correction: { ...current.tcp.applied_correction, state },
            },
          }))
        }
      />
      <TextInput
        fullWidth
        aria-label="기적용 보정 설명"
        placeholder="원본에 반영한 보정과 남은 이동의 관계"
        value={tcp.applied_correction.description ?? ""}
        onChange={(event) =>
          onChange((current) => ({
            ...current,
            tcp: {
              ...current.tcp,
              applied_correction: {
                ...current.tcp.applied_correction,
                description: textOrNull(event.target.value),
              },
            },
          }))
        }
      />
      <EvidenceDetails label="원점" evidence={tcp.origin.evidence} />
      <EvidenceDetails label="변환" evidence={tcp.transform.evidence} />
      <EvidenceDetails label="기적용 보정" evidence={tcp.applied_correction.evidence} />
    </fieldset>
  );
}
